import * as Mui from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { customTheme } from "../../../../../config/theme/MuiTheme";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import FormInput from "../../common/FormInput";
import { CreateDamageFormTypes } from "../Types";
import { useForm } from "react-hook-form";
import { nameof } from "../../common/ObjectUtils";
import { useEffect } from "react";
import { convertUtcToTzIsoDayJs } from "../../../../../util/timezoneConversion";
import { useRecoilValue } from "recoil";
import { generalInfoStepTimezoneState } from "./GeneralInfoStep";

const RentalAgreementStep = () => {
    const t = useOperationTranslation();

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as CreateDamageFormTypes.ISharedLocationState;

    const timezone = useRecoilValue(generalInfoStepTimezoneState);

    const form = useForm<CreateDamageFormTypes.IRentalAgreement>({
        defaultValues: {
            ...locationState.rentalAgreement,
            checkoutDate: convertUtcToTzIsoDayJs(
                locationState.rentalAgreement?.checkoutDate,
                timezone
            )?.toISOString(),
            expectedCheckinDate: convertUtcToTzIsoDayJs(
                locationState.rentalAgreement?.expectedCheckinDate,
                timezone
            )?.toISOString(),
            checkinDate: convertUtcToTzIsoDayJs(
                locationState.rentalAgreement?.checkinDate,
                timezone
            )?.toISOString(),
            isAssociated: locationState?.rentalAgreement?.isAssociated || (locationState?.rentalAgreement?.raId ? true : false)
        }
    });

    const getActualState = (data?: CreateDamageFormTypes.IRentalAgreement): CreateDamageFormTypes.ISharedLocationState => {
        return {
            generalInfo: locationState.generalInfo,
            rentalAgreement: data || form.getValues(),
        }
    }

    const handleNext = (data: CreateDamageFormTypes.IRentalAgreement) => {
        navigate("/operation/damages/forms/creation/damages", { state: getActualState(data) });
    }

    const goBack = () => {
        navigate("/operation/damages/forms/creation/general-info", { state: locationState });
    }

    useEffect(() => {

        if (locationState?.rentalAgreement?.isAssociated != undefined)
            form.setValue("isAssociated", locationState?.rentalAgreement?.isAssociated);

    }, []);


    return (
        <>
            <Mui.Card>
                <Mui.CardHeader
                    title={t("damageForm.damageFormCreation.rentalAgreement.titleCard")}
                />

                <form onSubmit={form.handleSubmit(handleNext)}>
                    <Mui.CardContent>
                        <Mui.Grid
                            container
                            alignItems="center"
                            spacing={2.5}
                        >
                            <Mui.Grid item xl={12} lg={12} md={12} xs={12}>
                                <FormInput
                                    type="text"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("raId")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raId")}
                                    disabled
                                />
                            </Mui.Grid>
                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="text"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("raOwner")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raHolder")}
                                    disabled
                                />
                            </Mui.Grid>
                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="text"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("raNumber")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raNumber")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="date"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("checkoutDate")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raStartDate")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="date"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("expectedCheckinDate")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raExpectedEndDate")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="date"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("checkinDate")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.raActualEndDate")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="text"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("checkoutDMS")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.startLocation")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="text"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("checkinDMS")}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.damageFormCreation.rentalAgreement.arrivalLocation")}
                                    disabled
                                />
                            </Mui.Grid>

                            <Mui.Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
                                <FormInput
                                    type="checkbox"
                                    name={nameof<CreateDamageFormTypes.IRentalAgreement>("isAssociated")}
                                    formInstance={form}
                                    fullWidth
                                    disabled={locationState?.rentalAgreement?.raId === undefined || locationState?.rentalAgreement?.raId === null}
                                    label={t("damageForm.damageFormCreation.rentalAgreement.associateRaToThisForm")}
                                />
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.CardContent>
                    <Mui.CardActions>
                        <Mui.Grid container spacing={2} justifyContent="end">
                            <Mui.Grid item xs={12} md={"auto"}>
                                <Mui.Button
                                    variant="outlined"
                                    fullWidth
                                    size="medium"
                                    onClick={goBack}
                                >
                                    <Mui.Typography color={customTheme.palette.accent.main}>
                                        {t("damageForm.damageFormCreation.button.back")}
                                    </Mui.Typography>
                                </Mui.Button>
                            </Mui.Grid>

                            <Mui.Grid item xs={12} md={"auto"}>
                                <Mui.Button
                                    variant="contained"
                                    fullWidth
                                    type="submit"
                                    // onClick={handleNext}
                                    size="medium"
                                >
                                    <Mui.Typography variant="h6" color={'accent'}>
                                        {t("damageForm.damageFormCreation.button.next")}
                                    </Mui.Typography>
                                </Mui.Button>
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.CardActions>
                </form>
            </Mui.Card>
        </>
    )
}
export default RentalAgreementStep;